import React, { useState } from 'react';
import {
  SalvBanner,
  SalvBannerType,
  SalvButton,
  SalvFieldHeader,
  SalvModal,
  SalvTagsInput,
  DatePicker,
} from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { Dayjs } from 'dayjs';
import { showFeedback } from '../../feedback/feedbackReducer';
import { parseErrorMessage } from '../../common/errors/helpers';
import { useRerunScenariosForTransactionsByCreatedTimeRange } from './scenarioRerunActions';

interface Props {
  show: boolean;
  onClose: () => void;
}

interface FormValues {
  scenarioHandles: string[];
  createdFrom: Dayjs | null;
  createdTo: Dayjs | null;
}

const validationSchema = Yup.object().shape({
  scenarioHandles: Yup.array()
    .of(Yup.string().required())
    .min(1, 'At least one scenario handle is required')
    .required(),
  createdFrom: Yup.mixed<Dayjs>().required('Created from is required'),
  createdTo: Yup.mixed<Dayjs>().required('Created to is required'),
});

const RerunByTxCreatedTimeRangeModal = ({ show, onClose }: Props) => {
  const dispatch = useDispatch();
  const mutation = useRerunScenariosForTransactionsByCreatedTimeRange();
  const [error, setError] = useState<string | null>(null);

  const initialValues: FormValues = { scenarioHandles: [], createdFrom: null, createdTo: null };

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    setError(null);
    try {
      await mutation.mutateAsync({
        scenarioHandles: values.scenarioHandles,
        createdFrom: values.createdFrom!.toDate(),
        createdTo: values.createdTo!.toDate(),
      });
      dispatch(showFeedback({ message: 'Scenario rerun by created time range triggered successfully', success: true }));
      onClose();
      resetForm();
    } catch (e) {
      setError(await parseErrorMessage(e));
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} validateOnMount onSubmit={handleSubmit}>
      {({ values, errors, isValid, isSubmitting, setFieldValue, resetForm }) => (
        <SalvModal
          show={show}
          onHide={() => {
            resetForm();
            setError(null);
            onClose();
          }}
        >
          <Form>
            <SalvModal.Header>Rerun Scenarios for Transactions by Created Time Range</SalvModal.Header>
            <SalvModal.Body>
              {error && <SalvBanner message={error} type={SalvBannerType.DANGER} clearable className='m-b-16' />}
              <SalvTagsInput
                value={values.scenarioHandles}
                onChange={(newValues) => setFieldValue('scenarioHandles', newValues)}
                label='Scenario Handles'
                placeholder='Enter scenario handle'
                isInvalid={!!errors.scenarioHandles}
                className='m-b-16'
              />
              <SalvFieldHeader label='Created From' className='m-b-4' />
              <DatePicker
                value={values.createdFrom}
                onChange={(date: Dayjs | null) => setFieldValue('createdFrom', date)}
                showTime
                className='m-b-16'
              />
              <SalvFieldHeader label='Created To' className='m-b-4' />
              <DatePicker
                value={values.createdTo}
                onChange={(date: Dayjs | null) => setFieldValue('createdTo', date)}
                showTime
              />
            </SalvModal.Body>
            <SalvModal.Footer>
              <SalvButton
                variant='muted'
                onClick={() => {
                  resetForm();
                  setError(null);
                  onClose();
                }}
                label='Cancel'
              />
              <SalvButton label='Rerun' type='submit' disabled={!isValid || isSubmitting || mutation.isPending} />
            </SalvModal.Footer>
          </Form>
        </SalvModal>
      )}
    </Formik>
  );
};

export default RerunByTxCreatedTimeRangeModal;
